import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { checkEmailConfirmed } from "@/lib/auth/verify-email.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  CheckCircle2,
  MailCheck,
  ShieldCheck,
  RefreshCw,
  ArrowRight,
  Loader2,
  Smartphone,
} from "lucide-react";
import logoAsset from "@/assets/fixhen-logo.png.asset.json";

const search = z.object({
  email: z.string().optional(),
  redirect: z
    .string()
    .optional()
    .refine((r) => !r || (r.startsWith("/") && !r.startsWith("//")), { message: "Invalid redirect" }),
});

function safeRedirect(r: string | undefined): string {
  return r && r.startsWith("/") && !r.startsWith("//") ? r : "/account";
}

const CHANNEL = "fixhen:email-verified";

export const Route = createFileRoute("/verify-email")({
  ssr: false,
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "Verify your email — FIXHEN" },
      {
        name: "description",
        content: "Confirm your FIXHEN email address to secure your account and unlock ordering.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { email: qEmail, redirect } = useSearch({ from: "/verify-email" });
  const navigate = useNavigate();

  const [status, setStatus] = useState<"checking" | "confirmed" | "pending">("checking");
  const [hasSession, setHasSession] = useState(false);
  const [email, setEmail] = useState(qEmail ?? "");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [autoIn, setAutoIn] = useState(4);

  const emailRef = useRef(email);
  emailRef.current = email;
  const doneRef = useRef(false);
  const startedAt = useRef(Date.now());

  const markConfirmed = useCallback(async (session: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setStatus("confirmed");
    setHasSession(session);
    try {
      localStorage.setItem(CHANNEL, String(Date.now()));
      new BroadcastChannel(CHANNEL).postMessage("confirmed");
    } catch {}
    if (session) {
      // Refresh the JWT so email_confirmed_at lands in the local claims immediately.
      try { await supabase.auth.refreshSession(); } catch {}
      try { await supabase.rpc("claim_my_orders"); } catch {}
    }
  }, []);

  /** One verification probe: session-side first, then a cross-device probe by email. */
  const probe = useCallback(
    async (opts?: { manual?: boolean }) => {
      if (doneRef.current) return;
      if (opts?.manual) setProbing(true);
      try {
        const { data } = await supabase.auth.getUser();
        const user = data?.user ?? null;
        setHasSession(!!user);
        if (user?.email && !emailRef.current) setEmail(user.email);

        if (user?.email_confirmed_at) {
          await markConfirmed(true);
          return;
        }

        // Cross-device: the link may have been opened on a phone while this
        // browser still holds an unconfirmed (or no) session.
        const addr = (user?.email || emailRef.current || "").trim();
        if (addr && /.+@.+\..+/.test(addr)) {
          const res = await checkEmailConfirmed({ data: { email: addr } });
          if (res?.confirmed) {
            if (user) {
              // Pull a fresh token, then re-read the (now confirmed) user.
              try { await supabase.auth.refreshSession(); } catch {}
              const { data: fresh } = await supabase.auth.getUser();
              await markConfirmed(!!fresh?.user);
            } else {
              await markConfirmed(false);
            }
            return;
          }
        }
        setStatus("pending");
      } catch {
        setStatus((s) => (s === "checking" ? "pending" : s));
      } finally {
        setLastChecked(new Date());
        if (opts?.manual) setProbing(false);
      }
    },
    [markConfirmed],
  );

  // Adaptive polling: fast right after signup, slower later, paused while hidden.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const loop = async () => {
      if (cancelled || doneRef.current) return;
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        await probe();
      }
      if (cancelled || doneRef.current) return;
      const elapsed = Date.now() - startedAt.current;
      timer = setTimeout(loop, elapsed < 90_000 ? 3000 : 8000);
    };
    loop();

    const onWake = () => {
      if (doneRef.current) return;
      if (document.visibilityState === "visible") probe();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);

    // Same-device, other-tab sync (e.g. link opened in a second tab).
    const onStorage = (e: StorageEvent) => {
      if (e.key === CHANNEL) probe();
    };
    window.addEventListener("storage", onStorage);
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = () => probe();
    } catch {}

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user?.email_confirmed_at) markConfirmed(true);
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
      window.removeEventListener("storage", onStorage);
      try { bc?.close(); } catch {}
      sub.subscription.unsubscribe();
    };
  }, [probe, markConfirmed]);

  // Auto-continue once verified in a session-backed tab.
  useEffect(() => {
    if (status !== "confirmed" || !hasSession) return;
    if (autoIn <= 0) {
      navigate({ to: safeRedirect(redirect) });
      return;
    }
    const t = setTimeout(() => setAutoIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [status, hasSession, autoIn, navigate, redirect]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function resend() {
    if (!email) return toast.error("Enter your email address first");
    setBusy(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/verify-email${
            redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""
          }`,
        },
      });
      if (error) throw error;
      toast.success("Confirmation email sent — check your inbox.");
      setCooldown(45);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not send confirmation email");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-6 py-10">
        <Link to="/" className="mb-8 inline-flex items-center gap-2">
          <img src={logoAsset.url} alt="FIXHEN" width={32} height={32} className="h-8 w-8" />
          <span className="text-xl font-black tracking-tight text-ink">FIXHEN</span>
        </Link>

        {status === "confirmed" ? (
          <div className="w-full rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Email verified</h1>
            <p className="mt-2 text-sm text-slate-600">
              Thanks{email ? `, ${email}` : ""}. Your account is now secure and fully activated.
            </p>

            {hasSession ? (
              <>
                <Button
                  onClick={() => navigate({ to: safeRedirect(redirect) })}
                  className="mt-6 h-11 w-full bg-[#2563EB] text-base font-semibold hover:bg-[#1d4ed8]"
                >
                  Continue to my account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="mt-3 text-xs text-slate-500">
                  Redirecting automatically in {Math.max(autoIn, 0)}s…
                </p>
              </>
            ) : (
              <>
                <div className="mt-5 rounded-lg bg-emerald-50 px-4 py-3 text-left text-xs text-emerald-900">
                  Verified on another device. Sign in here to continue on this computer.
                </div>
                <Link
                  to="/auth"
                  search={{ mode: "signin", redirect } as never}
                  className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md bg-[#2563EB] text-base font-semibold text-white hover:bg-[#1d4ed8]"
                >
                  Sign in
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#2563EB]/10 text-[#2563EB]">
              <MailCheck className="h-8 w-8" />
            </div>
            <h1 className="text-center text-2xl font-bold text-slate-900">Confirm your email</h1>
            <p className="mt-2 text-center text-sm text-slate-600">
              We sent a secure confirmation link to
              {email ? (
                <>
                  {" "}
                  <strong className="text-slate-900">{email}</strong>.
                </>
              ) : (
                " your inbox."
              )}{" "}
              Click the link to activate your account.
            </p>

            <div className="mt-5 flex items-center justify-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2563EB]" />
              <span>
                Waiting for confirmation — this page updates by itself
                {lastChecked ? ` · last check ${lastChecked.toLocaleTimeString()}` : ""}
              </span>
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-lg border border-[#2563EB]/20 bg-[#2563EB]/5 p-3 text-xs text-slate-700">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
              <span>
                Opened the link on your phone? Keep this tab open — it detects the confirmation and
                continues here automatically.
              </span>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="v-email">Email</Label>
                <Input
                  id="v-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  onClick={() => probe({ manual: true })}
                  disabled={probing}
                  className="h-11 w-full bg-[#2563EB] font-semibold hover:bg-[#1d4ed8]"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${probing ? "animate-spin" : ""}`} />
                  I've confirmed — check now
                </Button>
                <Button
                  type="button"
                  onClick={resend}
                  disabled={busy || cooldown > 0}
                  variant="outline"
                  className="h-11 w-full"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
                </Button>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-slate-50 p-4 text-xs text-slate-600">
              <p className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
                <span>
                  Not seeing it? Check your spam folder, then look for a message from{" "}
                  <strong>info@contact.fixhen.com</strong>. The link expires after 60 minutes.
                </span>
              </p>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">
              Wrong address?{" "}
              <Link to="/auth" className="font-semibold text-[#2563EB] hover:underline">
                Sign in with a different account
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
